'use client'

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useInfiniteScroll } from '@dc-shared-web/hooks'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@dc-shared-web/components/shadcn/command'

import { Chip } from '@dc-shared-web/components/base/Chip'

import { cn } from '@dc-shared-web/lib'
import { preventDefault } from '@dc-shared-web/lib/utils'

export type MultiSelectOption = {
  value: string
  label: string
  uuid?: string
  secondLabel?: string
}

type MultiSelectProps = {
  options: MultiSelectOption[]
  emptyText?: string
  placeholder?: string
  allowNew?: boolean
  onScrollEndReached?: () => void
  setSearchValue?: (v: string) => void
  isLoading?: boolean
  searchValue?: string
  isError?: boolean
  disabled?: boolean
  onBlur?: () => void
  value?: {
    value: string
    label: string
    uuid?: string
  }[]
  onValueChange?: (
    value: {
      value: string
      label: string
      uuid?: string
    }[]
  ) => void
  'data-testid'?: string
  labelClassName?: string
  inputWrapperClassName?: string
  inputClassName?: string
}

const MultiSelect = (
  props: MultiSelectProps,
  ref: React.ForwardedRef<HTMLInputElement>
) => {
  const {
    value,
    onValueChange,
    options,
    emptyText,
    placeholder,
    allowNew,
    onScrollEndReached = () => {},
    setSearchValue,
    isError,
    disabled,
    onBlur,
    labelClassName,
    inputWrapperClassName,
    inputClassName,
    ...otherProps
  } = props
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const [highlightedValue, setHighlightedValue] = useState<string | undefined>()

  const filteredOptions = useMemo(() => {
    return options.filter((option) => {
      return (
        !value?.map((v) => v.value)?.includes(option.value) &&
        (option.label
          ?.toLocaleLowerCase()
          ?.includes(search.toLocaleLowerCase()) ||
          option.value
            ?.toLocaleLowerCase()
            ?.includes(search.toLocaleLowerCase()))
      )
    })
  }, [search, value, options])

  const handleMultipleSelect = (option: MultiSelectOption) => {
    if (value?.map((v) => v.value)?.includes(option.value)) {
      if (value.length === 1) return false
      onValueChange?.(value.filter((value) => value.value !== option.value))
    } else {
      onValueChange?.([
        ...(value ?? []),
        {
          value: option.value,
          label: option.label,
          uuid: option.uuid,
        },
      ])
    }
    setSearch('')
  }

  const onSelectItem = (selectedValue: string) => {
    const option = options.find(
      (option) => option.value.toLowerCase().trim() === selectedValue
    )

    if (option) {
      handleMultipleSelect(option)
    }
  }

  const onSelectNew = () => {
    handleMultipleSelect({
      value: search,
      label: search,
      uuid: options.filter((value) => value.label === search)[0]?.uuid,
    })
  }

  const onSearch = (e: React.FormEvent<HTMLInputElement>) => {
    setSearch(e.currentTarget.value)
    setSearchValue?.(e.currentTarget.value)
  }

  const onScroll = useInfiniteScroll(50, onScrollEndReached)

  const handleChipRemoval = (chipValue: string) => {
    const filterResults = value?.filter((val) => val.value !== chipValue)
    onValueChange?.(filterResults || [])
  }

  const handleBackspaceKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (search === '' && value?.length) {
      handleChipRemoval(value[value.length - 1].value)
      preventDefault(e)
    }
  }

  const handleEscapeKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    setOpen(false)
    inputRef.current?.blur()
    preventDefault(e)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'Backspace':
        handleBackspaceKey(e)
        break
      case 'Escape':
        handleEscapeKey(e)
        break
    }
  }

  useEffect(() => {
    setSearchValue?.(search)
  }, [search, setSearchValue])

  useEffect(() => {
    setHighlightedValue(filteredOptions[0]?.value.toLowerCase().trim())
  }, [filteredOptions])

  useImperativeHandle(ref, () => inputRef.current!, [])

  return (
    <Command
      shouldFilter={false}
      className='tw-overflow-visible bg-transparent tw-h-auto'
      onKeyDown={handleKeyDown}
      value={highlightedValue}
      onValueChange={(value) => setHighlightedValue(value)}
    >
      <div
        className={cn(
          'tw-border-[1px] tw-rounded-md tw-bg-background tw-border-neutral-200 tw-flex tw-flex-wrap tw-p-2 tw-gap-y-1 tw-max-h-[110px] tw-overflow-auto',
          open && 'tw-rounded-b-none',
          isError && 'tw-border-red-600'
        )}
      >
        {value?.map((val, index) => (
          <Chip key={index} value={val} onRemove={handleChipRemoval} />
        ))}
        <CommandInput
          ref={inputRef}
          wrapperClassName={cn(
            'tw-border-0 tw-grow tw-min-w-[120px] tw-px-0 tw-px-0',
            inputWrapperClassName
          )}
          className={cn(
            'tw-p-0 tw-h-7 placeholder:tw-text-neutral-500 focus-visible:tw-ring-0',
            inputClassName
          )}
          placeholder={placeholder ?? 'Search for an option'}
          value={search}
          onInput={onSearch}
          onBlur={() => {
            setOpen(false)
            setSearch('')
            onBlur?.()
          }}
          onFocus={() => {
            if (filteredOptions.length > 0 || allowNew) {
              setOpen(true)
            }
          }}
          showIcon={false}
          disabled={disabled}
          {...otherProps}
        />
      </div>

      <div className='tw-relative'>
        {open && (
          <div
            className='tw-absolute tw-w-full tw-z-10 tw-top-0 tw-rounded-md tw-rounded-t-none tw-border tw-border-t-0 tw-bg-popover tw-text-popover-foreground tw-shadow-md tw-outline-none tw-animate-in tw-pt-1'
            data-testid='share-modal-multi-select-content'
          >
            <CommandGroup
              onScroll={onScroll}
              className={cn(
                filteredOptions.length && 'tw-max-h-[200px] tw-overflow-auto'
              )}
              style={{ scrollbarColor: 'auto' }}
            >
              {!allowNew && (
                <CommandEmpty>{emptyText ?? 'No results found'}</CommandEmpty>
              )}
              <div className={cn(filteredOptions.length && 'tw-max-h-[200px]')}>
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value.toLowerCase().trim()}
                    onMouseDown={preventDefault}
                    onSelect={onSelectItem}
                  >
                    {option.secondLabel ? (
                      <div className='tw-flex tw-flex-col tw-px-2'>
                        <div
                          className={cn(
                            'tw-text-sm tw-font-medium',
                            labelClassName
                          )}
                        >
                          {option.label}
                        </div>
                        <div className='tw-text-sm'>{option.secondLabel}</div>
                      </div>
                    ) : (
                      <div
                        className={cn(
                          'tw-text-sm tw-font-medium tw-px-2',
                          labelClassName
                        )}
                      >
                        {option.label}
                      </div>
                    )}
                  </CommandItem>
                ))}
                {allowNew && search && (
                  <CommandItem
                    value={search}
                    onSelect={onSelectNew}
                    onMouseDown={preventDefault}
                  >
                    {`Use this address: ${search}`}
                  </CommandItem>
                )}
              </div>
            </CommandGroup>
          </div>
        )}
      </div>
    </Command>
  )
}

MultiSelect.displayName = 'MultiSelect'
export default forwardRef(MultiSelect)
